import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

const MOBILE_MQ = '(max-width: 989px)';

/**
 * Mobile / tablet fullscreen facets: category list → drill-down. Disables the duplicate
 * filter inputs in the desktop bar or modal slices based on viewport width.
 */
class FacetsMobileModalComponent extends Component {
  requiredRefs = ['menuLayer', 'detailLayer'];

  static ACTIVE_FACET_STORAGE_PREFIX = 'facets-mobile-modal-active:';

  /** @type {HTMLElement | null} */
  #dialogHost = null;
  /** @type {MutationObserver | null} */
  #domObserver = null;
  #restoreQueued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#dialogHost = this.closest('dialog-component');
    this.#dialogHost?.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    window.addEventListener('resize', this.#debouncedSync);
    this.#observeDomResets();
    this.#syncFacetSets();
    this.#restoreActiveFacet();
  }

  disconnectedCallback() {
    this.#dialogHost?.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    window.removeEventListener('resize', this.#debouncedSync);
    this.#domObserver?.disconnect();
    this.#domObserver = null;
    super.disconnectedCallback();
  }

  #onDialogOpen = () => {
    this.#restoreActiveFacet();
  };

  #onFilterUpdate = () => {
    // Section morph happens asynchronously after the filter event.
    this.#queueRestoreActiveFacet();
    window.setTimeout(() => this.#queueRestoreActiveFacet(), 80);
    window.setTimeout(() => this.#queueRestoreActiveFacet(), 180);
  };

  #debouncedSync = debounce(() => this.#syncFacetSets(), 100);

  #syncFacetSets() {
    const form = this.closest('form');
    if (!form) return;

    const mobile = window.matchMedia(MOBILE_MQ).matches;
    const desktopWrap = form.querySelector('.facets__filters-wrapper--desktop-bar');
    const slices = form.querySelector('.facets-mobile-modal__slices');

    const setDisabled = (root, disabled) => {
      if (!root) return;
      root.querySelectorAll('input, select, textarea, button').forEach((el) => {
        if (el instanceof HTMLButtonElement && el.type === 'submit') return;
        el.disabled = disabled;
      });
    };

    setDisabled(desktopWrap, mobile);
    setDisabled(slices, !mobile);
  }

  /**
   * @param {MouseEvent} event
   */
  openFacetSlice = (event) => {
    const btn = event.target.closest('[data-facet-param]');
    if (!(btn instanceof HTMLElement)) return;

    const param = btn.dataset.facetParam;
    const label = btn.dataset.facetLabel || '';
    const { menuLayer, detailLayer, detailTitle } = this.refs;

    if (!menuLayer || !detailLayer) return;

    this.#showFacetDetail(param, label);
    this.#setActiveFacet(param, label);
  };

  showFacetMenu = () => {
    const { menuLayer, detailLayer } = this.refs;
    if (menuLayer) menuLayer.hidden = false;
    if (detailLayer) detailLayer.hidden = true;

    this.querySelectorAll('.facets-mobile-modal__slice').forEach((slice) => {
      slice.hidden = true;
      const det = slice.querySelector('details');
      if (det instanceof HTMLDetailsElement) {
        det.open = false;
      }
    });

    this.#clearActiveFacet();
  };

  /**
   * @param {string} param
   * @param {string} label
   */
  #showFacetDetail(param, label) {
    const { menuLayer, detailLayer, detailTitle } = this.refs;
    if (!menuLayer || !detailLayer) return;

    menuLayer.hidden = true;
    detailLayer.hidden = false;

    if (detailTitle instanceof HTMLElement) {
      detailTitle.textContent = label;
    }

    this.querySelectorAll('.facets-mobile-modal__slice').forEach((slice) => {
      const match = slice.getAttribute('data-facet-param') === param;
      slice.hidden = !match;
      const det = slice.querySelector('details');
      if (det instanceof HTMLDetailsElement) {
        det.open = match;
      }
    });
  }

  #storageKey() {
    const dialogId = this.#dialogHost?.id || this.closest('[id]')?.id || 'default';
    return `${FacetsMobileModalComponent.ACTIVE_FACET_STORAGE_PREFIX}${dialogId}`;
  }

  /**
   * @param {string} param
   * @param {string} label
   */
  #setActiveFacet(param, label) {
    sessionStorage.setItem(this.#storageKey(), JSON.stringify({ param, label }));
  }

  #clearActiveFacet() {
    sessionStorage.removeItem(this.#storageKey());
  }

  #restoreActiveFacet() {
    const raw = sessionStorage.getItem(this.#storageKey());
    if (!raw) return false;

    try {
      const { param, label } = JSON.parse(raw);
      if (!param) return false;

      const targetSlice = this.querySelector(`.facets-mobile-modal__slice[data-facet-param="${CSS.escape(param)}"]`);
      if (!(targetSlice instanceof HTMLElement)) return false;

      const resolvedLabel =
        typeof label === 'string' && label.length > 0
          ? label
          : this.querySelector(`.facets-mobile-modal__menu-row[data-facet-param="${CSS.escape(param)}"]`)?.getAttribute(
              'data-facet-label',
            ) || '';

      this.#showFacetDetail(param, resolvedLabel);
      return true;
    } catch {
      return false;
    }
  }

  #observeDomResets() {
    if (this.#domObserver) return;

    this.#domObserver = new MutationObserver(() => {
      this.#queueRestoreActiveFacet();
    });

    this.#domObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'open'],
    });
  }

  #queueRestoreActiveFacet() {
    if (this.#restoreQueued) return;
    this.#restoreQueued = true;

    requestAnimationFrame(() => {
      this.#restoreQueued = false;
      if (!this.isConnected) return;
      this.#restoreActiveFacet();
    });
  }
}

if (!customElements.get('facets-mobile-modal-component')) {
  customElements.define('facets-mobile-modal-component', FacetsMobileModalComponent);
}
